import { singleton } from "tsyringe";
import type {
    AiTutorAssistantSessionMessageContentsDto,
    AiTutorAssistantSessionMessageCompletedDto,
    AiTutorSessionMessageDto,
    AiTutorSystemSessionMessageDto,
    AiTutorUserSessionMessageDto,
    AiTutorSessionStudySourceDto,
    AiTutorSessionSourceDto,
    AiTutorSessionConceptEnhancementSourceDto,
    AiTutorSessionStudyReviewSourceDto,
    AiTutorSessionChallengeSourceDto,
} from "@/entities/ai-tutor-session/domain/dtos";
import {
    AiTutorSessionMessageRole,
    AiTutorAssistantSessionMessageStatus,
    AiTutorSessionSourceType,
} from "@/entities/ai-tutor-session/domain/enums";

export interface AiTutorSessionBehaviorStructure {
    isUserSessionMessage: (
        sessionMessage: AiTutorSessionMessageDto,
    ) => sessionMessage is AiTutorUserSessionMessageDto;
    isAssistantSessionMessage: (
        sessionMessage: AiTutorSessionMessageDto,
    ) => sessionMessage is AiTutorAssistantSessionMessageContentsDto;
    isSystemSessionMessage: (
        sessionMessage: AiTutorSessionMessageDto,
    ) => sessionMessage is AiTutorSystemSessionMessageDto;
    isAssistantSessionMessageCompleted: (
        sessionMessage: AiTutorSessionMessageDto,
    ) => sessionMessage is AiTutorAssistantSessionMessageCompletedDto;
    isStudySource: (dto: AiTutorSessionSourceDto) => dto is AiTutorSessionStudySourceDto;
    isStudyReviewSource: (
        dto: AiTutorSessionSourceDto,
    ) => dto is AiTutorSessionStudyReviewSourceDto;
    isChallengeSource: (dto: AiTutorSessionSourceDto) => dto is AiTutorSessionChallengeSourceDto;
    isConceptEnhancementSource: (
        dto: AiTutorSessionSourceDto,
    ) => dto is AiTutorSessionConceptEnhancementSourceDto;
}

@singleton()
export class AiTutorSessionBehavior implements AiTutorSessionBehaviorStructure {
    public isUserSessionMessage = (
        sessionMessage: AiTutorSessionMessageDto,
    ): sessionMessage is AiTutorUserSessionMessageDto => {
        return sessionMessage.role === AiTutorSessionMessageRole.User;
    };

    public isAssistantSessionMessage = (
        sessionMessage: AiTutorSessionMessageDto,
    ): sessionMessage is AiTutorAssistantSessionMessageContentsDto => {
        return sessionMessage.role === AiTutorSessionMessageRole.Assistant;
    };

    public isSystemSessionMessage = (
        sessionMessage: AiTutorSessionMessageDto,
    ): sessionMessage is AiTutorSystemSessionMessageDto => {
        return sessionMessage.role === AiTutorSessionMessageRole.System;
    };

    public isAssistantSessionMessageCompleted = (
        sessionMessage: AiTutorSessionMessageDto,
    ): sessionMessage is AiTutorAssistantSessionMessageCompletedDto => {
        return (
            sessionMessage.role === AiTutorSessionMessageRole.Assistant &&
            sessionMessage.status === AiTutorAssistantSessionMessageStatus.Completed
        );
    };

    public isStudySource = (dto: AiTutorSessionSourceDto): dto is AiTutorSessionStudySourceDto => {
        return dto.type === AiTutorSessionSourceType.Study;
    };

    public isStudyReviewSource = (
        dto: AiTutorSessionSourceDto,
    ): dto is AiTutorSessionStudyReviewSourceDto => {
        return dto.type === AiTutorSessionSourceType.StudyReview;
    };

    public isChallengeSource = (
        dto: AiTutorSessionSourceDto,
    ): dto is AiTutorSessionChallengeSourceDto => {
        return dto.type === AiTutorSessionSourceType.Challenge;
    };

    public isConceptEnhancementSource = (
        dto: AiTutorSessionSourceDto,
    ): dto is AiTutorSessionConceptEnhancementSourceDto => {
        return dto.type === AiTutorSessionSourceType.ConceptEnhancement;
    };
}
